import { formatAmountDisplay } from '@/lib/formatAmount'

export type CurrencyOption = {
  code: string
  label: string
}

/** 의료관광·제휴 정산에서 자주 쓰는 통화 (상단 배치) */
export const COMMON_CURRENCIES: CurrencyOption[] = [
  { code: 'KRW', label: 'KRW — 원화 (₩)' },
  { code: 'USD', label: 'USD — 미국 달러 ($)' },
  { code: 'JPY', label: 'JPY — 일본 엔 (¥)' },
  { code: 'CNY', label: 'CNY — 중국 위안 (¥)' },
  { code: 'THB', label: 'THB — 태국 바트 (฿)' },
  { code: 'EUR', label: 'EUR — 유로 (€)' },
  { code: 'GBP', label: 'GBP — 영국 파운드 (£)' },
  { code: 'AUD', label: 'AUD — 호주 달러 (A$)' },
  { code: 'CAD', label: 'CAD — 캐나다 달러 (C$)' },
  { code: 'SGD', label: 'SGD — 싱가포르 달러 (S$)' },
  { code: 'HKD', label: 'HKD — 홍콩 달러 (HK$)' },
  { code: 'TWD', label: 'TWD — 대만 달러 (NT$)' },
  { code: 'VND', label: 'VND — 베트남 동 (₫)' },
  { code: 'MYR', label: 'MYR — 말레이시아 링깃 (RM)' },
  { code: 'IDR', label: 'IDR — 인도네시아 루피아 (Rp)' },
  { code: 'PHP', label: 'PHP — 필리핀 페소 (₱)' },
]

export const OTHER_CURRENCIES: CurrencyOption[] = [
  { code: 'AED', label: 'AED — UAE 디르함' },
  { code: 'CHF', label: 'CHF — 스위스 프랑' },
  { code: 'INR', label: 'INR — 인도 루피 (₹)' },
  { code: 'KZT', label: 'KZT — 카자흐스탄 텡게 (₸)' },
  { code: 'MNT', label: 'MNT — 몽골 투그릭 (₮)' },
  { code: 'MOP', label: 'MOP — 마카오 파타카' },
  { code: 'NZD', label: 'NZD — 뉴질랜드 달러 (NZ$)' },
  { code: 'QAR', label: 'QAR — 카타르 리얄' },
  { code: 'RUB', label: 'RUB — 러시아 루블 (₽)' },
  { code: 'SAR', label: 'SAR — 사우디 리얄' },
  { code: 'SEK', label: 'SEK — 스웨덴 크로나 (kr)' },
  { code: 'TRY', label: 'TRY — 터키 리라 (₺)' },
  { code: 'UAH', label: 'UAH — 우크라이나 히르니아 (₴)' },
  { code: 'UZS', label: 'UZS — 우즈베키스탄 솜' },
]

export const CURRENCY_SELECT_OPTIONS = [
  ...COMMON_CURRENCIES.map((c) => ({ label: c.label, value: c.code })),
  ...OTHER_CURRENCIES.map((c) => ({ label: c.label, value: c.code })),
]

export const CURRENCY_ORDER = [
  ...COMMON_CURRENCIES.map((c) => c.code),
  ...OTHER_CURRENCIES.map((c) => c.code),
]

const CURRENCY_SYMBOLS: Record<string, string> = {
  KRW: '₩',
  USD: '$',
  JPY: '¥',
  CNY: '¥',
  THB: '฿',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
  HKD: 'HK$',
  TWD: 'NT$',
  VND: '₫',
  MYR: 'RM',
  IDR: 'Rp',
  PHP: '₱',
  INR: '₹',
  RUB: '₽',
  TRY: '₺',
  UAH: '₴',
  KZT: '₸',
  MNT: '₮',
}

export function formatCurrencyAmount(
  currency: string,
  amount: string | number,
): string {
  const formatted = formatAmountDisplay(amount)

  if (!formatted) {
    return `${currency} ${amount}`
  }

  const symbol = CURRENCY_SYMBOLS[currency]

  if (symbol) {
    return `${symbol}${formatted}`
  }

  return `${formatted} ${currency}`
}

export function sortByCurrencyOrder<
  T extends { currency: string },
>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const ai = CURRENCY_ORDER.indexOf(a.currency)
    const bi = CURRENCY_ORDER.indexOf(b.currency)
    const aRank = ai === -1 ? 999 : ai
    const bRank = bi === -1 ? 999 : bi
    if (aRank !== bRank) {
      return aRank - bRank
    }
    return a.currency.localeCompare(b.currency)
  })
}
